import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { BrandSiteAssetPickerTrigger } from "@/components/brand-site/BrandSiteAssetPicker";
import { BrandSiteStudio } from "@/components/brand-site/BrandSiteStudio";
import { AdminAlert } from "@/components/shell/AdminPage";
import { useHermes } from "@/components/hermes/HermesProvider";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  BrandSiteApiError,
  listBrandSiteCollections,
  listBrandSitePages,
  updateBrandSitePage,
  type BrandSiteCollection,
  type BrandSitePage,
  brandSiteAssetBaseUrl,
} from "@/lib/brand-site-api";
import { mergeBrandSitePageAfterDraftApply } from "@/lib/brand-site-draft-apply";
import { appendHtmlBeforeBodyClose } from "@/lib/materials-external-video";
import { buildMultiPickEditPrompt } from "@/lib/brand-site-pick-refs";
import { createKbApi } from "@/lib/kb-api";
import { useRefreshBrandSitePagesOnHermesDraft } from "@/lib/use-refresh-brand-site-pages-on-hermes-draft";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";

/** 设计工作室：左预览 + 右固定 AI（场景会话） */
export default function BrandSiteEditPage() {
  const { brandId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accountId } = useAccountWorkspace();
  const { currentSite } = useBrandSiteWorkbench();
  const site = currentSite;
  const {
    startBrandSiteStudioSession,
    stopBrandSiteStudio,
    brandSiteStudioActive,
    sendOrEnqueue,
    isSending,
    brandSiteDraftRevision,
    brandSiteDraftMutationHint,
    clearBrandSiteDraftMutationHint,
  } = useHermes();

  const [pages, setPages] = useState<BrandSitePage[]>([]);
  const [collections, setCollections] = useState<BrandSiteCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickSubmitting, setPickSubmitting] = useState(false);

  const selectedSlug = searchParams.get("page");
  const sourceFullscreenRequested = searchParams.get("fullscreen") === "1";
  const initialRightPanel = searchParams.get("source") === "1" ? ("source" as const) : undefined;
  const [sourceFullscreenOpen, setSourceFullscreenOpen] = useState(sourceFullscreenRequested);

  useEffect(() => {
    if (sourceFullscreenRequested) setSourceFullscreenOpen(true);
  }, [sourceFullscreenRequested]);

  const handleSourceFullscreenOpenChange = useCallback(
    (open: boolean) => {
      setSourceFullscreenOpen(open);
      if (open) return;
      const next = new URLSearchParams(searchParams);
      next.delete("fullscreen");
      next.delete("source");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const refreshPages = useCallback(
    async (brandSiteId: number) => {
      const [pagesPayload, collectionsPayload] = await Promise.all([
        listBrandSitePages(accountId, brandSiteId),
        listBrandSiteCollections(accountId, brandSiteId).catch(() => ({
          collections: [] as BrandSiteCollection[],
        })),
      ]);
      setPages(pagesPayload.pages ?? []);
      setCollections(collectionsPayload.collections ?? []);
      return pagesPayload.pages ?? [];
    },
    [accountId],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!brandId) return;
      const firstLoad = pages.length === 0 && collections.length === 0;
      if (firstLoad) setLoading(true);
      setError(null);
      try {
        await refreshPages(site.id);
        if (cancelled) return;

        let kbBindings: { wiki_id: number; access: "read" | "write" }[] = [];
        let enterpriseWikiId: number | undefined;
        try {
          const { wikis } = await createKbApi(accountId).listWikis();
          const enterprise = wikis.find((item) => item.kind === "enterprise");
          if (enterprise) {
            enterpriseWikiId = enterprise.id;
            kbBindings.push({ wiki_id: enterprise.id, access: "read" });
          }
          const wiki = wikis.find(
            (item) => item.kind === "brand" && String(item.brand_id) === String(brandId),
          );
          if (wiki) kbBindings.push({ wiki_id: wiki.id, access: "read" });
        } catch {
          // 无知识库仍可聊
        }

        await startBrandSiteStudioSession({
          mode: "brand_site_authoring",
          accountId,
          brandId: Number(brandId),
          brandSiteId: site.id,
          kbBindings,
          enterpriseWikiId,
        });
      } catch (caught) {
        if (cancelled) return;
        setError(caught instanceof BrandSiteApiError ? caught.message : "没法打开这一页");
        if (firstLoad) {
          setPages([]);
          setCollections([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      stopBrandSiteStudio();
    };
    // 刻意不把 hermes setter 放进依赖：引用抖动会反复卸载预览
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随账户/站切换重载
  }, [accountId, brandId, refreshPages, site.id]);

  useEffect(() => {
    void refreshPages(site.id).catch(() => undefined);
  }, [refreshPages, site.id]);

  const refreshPagesBound = useCallback(async () => {
    await refreshPages(site.id);
  }, [refreshPages, site.id]);

  useRefreshBrandSitePagesOnHermesDraft({
    enabled: Boolean(site),
    isSending,
    brandSiteDraftRevision,
    refresh: refreshPagesBound,
  });

  useEffect(() => {
    if (brandSiteDraftRevision < 1 || !brandSiteDraftMutationHint) return;
    const hint = brandSiteDraftMutationHint;
    if (hint.page) {
      setPages((current) => mergeBrandSitePageAfterDraftApply(current, hint.page));
    }
    const slug = hint.slug?.trim();
    if (slug) {
      setSearchParams((prev) => {
        if (prev.get("page") === slug) return prev;
        const next = new URLSearchParams(prev);
        next.set("page", slug);
        return next;
      });
    }
    clearBrandSiteDraftMutationHint();
  }, [
    brandSiteDraftMutationHint,
    brandSiteDraftRevision,
    clearBrandSiteDraftMutationHint,
    setSearchParams,
  ]);

  const handlePickSubmit = useCallback(
    async (payload: {
      userRequest: string;
      pageSlug: string;
      pageTitle: string | null;
      pagePath: string;
      picks: import("@/lib/brand-site-pick-refs").PickRef[];
    }) => {
      if (!brandId) return;
      setPickSubmitting(true);
      setError(null);
      try {
        if (!brandSiteStudioActive) {
          await startBrandSiteStudioSession({
            mode: "brand_site_authoring",
            accountId,
            brandId: Number(brandId),
            brandSiteId: site.id,
          });
        }
        await sendOrEnqueue(
          buildMultiPickEditPrompt({
            userRequest: payload.userRequest,
            pageSlug: payload.pageSlug,
            pageTitle: payload.pageTitle,
            pagePath: payload.pagePath,
            picks: payload.picks,
          }),
        );
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "点选发送失败");
      } finally {
        setPickSubmitting(false);
      }
    },
    [
      accountId,
      brandId,
      brandSiteStudioActive,
      sendOrEnqueue,
      site.id,
      startBrandSiteStudioSession,
    ],
  );

  const handleInsertHtml = useCallback(
    async (html: string) => {
      const slug =
        selectedSlug?.trim() ||
        pages.find((row) => row.is_home)?.slug ||
        pages[0]?.slug;
      const page = pages.find((row) => row.slug === slug);
      if (!page) {
        toast.error("请先选择要编辑的页面");
        throw new Error("no page");
      }
      const nextHtml = appendHtmlBeforeBodyClose(page.html_content ?? "", html);
      const { page: updated } = await updateBrandSitePage(accountId, site.id, page.id, {
        html_content: nextHtml,
        source: "human",
      });
      setPages((current) =>
        current.map((row) => (row.id === updated.id ? updated : row)),
      );
      if (updated.slug && updated.slug !== selectedSlug) {
        setSearchParams({ page: updated.slug });
      }
    },
    [accountId, pages, selectedSlug, setSearchParams, site.id],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {error ? (
        <div className="shrink-0 px-1 pb-2">
          <AdminAlert>{error}</AdminAlert>
        </div>
      ) : null}
      {loading && pages.length === 0 ? (
        <p className="p-2 text-sm text-muted-foreground">加载中…</p>
      ) : null}

      {pages.length > 0 || collections.length > 0 || !loading ? (
        <BrandSiteStudio
          accountId={accountId}
          brandSiteId={site.id}
          pages={pages}
          collections={collections}
          selectedSlug={selectedSlug}
          draftRevision={brandSiteDraftRevision}
          assetBaseUrl={brandSiteAssetBaseUrl(site)}
          onSelectSlug={(slug) => setSearchParams({ page: slug })}
          onPageSaved={(updated) => {
            setPages((current) => current.map((row) => (row.id === updated.id ? updated : row)));
          }}
          onPickSubmit={handlePickSubmit}
          pickSubmitting={pickSubmitting}
          initialRightPanel={initialRightPanel}
          sourceFullscreenOpen={sourceFullscreenOpen}
          onSourceFullscreenOpenChange={handleSourceFullscreenOpenChange}
          headerStart={
            <div className="min-w-0 space-y-0.5">
              <h1 className="truncate text-base font-semibold tracking-tight">
                设计「{site.title}」
              </h1>
              <p className="truncate text-xs text-muted-foreground">
                左预览 · 右 AI / 源代码 · 场景会话（点选元素即可改）
              </p>
            </div>
          }
          headerEnd={
            <BrandSiteAssetPickerTrigger
              accountId={accountId}
              brandId={Number(brandId)}
              brandSiteId={site.id}
              pageSlug={selectedSlug}
              publicUrl={site.public_url ?? null}
              onInsertHtml={handleInsertHtml}
            />
          }
        />
      ) : null}
    </div>
  );
}
